import type {
  CustomField,
  ExtendedProductVariant,
  Product,
  WovarProduct,
} from "@commercekit/api";
import { useMemo, useCallback } from "react";

const allowedAttributes = [
  "WVR1_General_Brand",
  "sku",
  "WVR1_ERP_EAN_code",
  "WVR1_General_Length",
  "WVR1_General_Diameter",
  "WVR1_General_Marketing_colour",
  "WVR1_Marketing_Feature_1",
  "WVR1_Marketing_Feature_2",
  "WVR1_Marketing_Feature_3",
];

const sortCustomField = (
  arr: CustomField[],
  allowedAttributes: string[]
): CustomField[] => {
  const attributeOrder = new Map<string, number>();
  allowedAttributes.forEach((attr, index) => {
    attributeOrder.set(attr, index);
  });

  return arr.slice().sort((a, b) => {
    const orderA = attributeOrder.get(a.name) ?? Infinity;
    const orderB = attributeOrder.get(b.name) ?? Infinity;
    return orderA - orderB;
  });
};

export const useProduct = (product: WovarProduct | null) => {
  const customFields = useMemo(() => {
    const allowedMarketingProperties = product?.marketingProperties
      ? product?.marketingProperties?.filter((data) =>
          allowedAttributes.includes(data.name)
        )
      : [];

    const combinedCustomFields = product?.customFields.concat(
      allowedMarketingProperties
    );
    const sortedCustomField = sortCustomField(
      combinedCustomFields || [],
      allowedAttributes
    );
    const productCustomFields =
      sortedCustomField?.map((item) => {
        return {
          name: item.displayName,
          val: item.value.stringValue || item.value.stringValues?.join(","),
        };
      }) || [];

    return productCustomFields.filter((item) => item.val);
  }, [product?.customFields, product?.marketingProperties]);

  const pickCustomField = useCallback(
    (name: string) => {
      return customFields.find((o) => o.name === name);
    },
    [customFields]
  );

  return { customFields, pickCustomField };
};

export const useWovarProduct = (
  product: WovarProduct | null,
  selectedProductVariant: ExtendedProductVariant | undefined
) => {
  const customFields = useMemo(() => {
    const allowedMarketingProperties =
      selectedProductVariant && selectedProductVariant?.marketingProperties
        ? selectedProductVariant?.marketingProperties?.filter((data) =>
            allowedAttributes.includes(data.name)
          )
        : product?.marketingProperties
        ? product?.marketingProperties?.filter((data) =>
            allowedAttributes.includes(data.name)
          )
        : [];

    const combinedCustomFields =
      selectedProductVariant && selectedProductVariant?.customFields
        ? selectedProductVariant?.customFields.concat(
            allowedMarketingProperties
          )
        : product?.customFields.concat(allowedMarketingProperties);

    const sortedCustomField = sortCustomField(
      combinedCustomFields || [],
      allowedAttributes
    );
    const productCustomFields =
      sortedCustomField?.map((item) => {
        return {
          name: item.displayName,
          val: item.value.stringValue || item.value.stringValues?.join(","),
        };
      }) || [];

    return productCustomFields.filter((item) => item.val);
  }, [
    product?.customFields,
    product?.marketingProperties,
    selectedProductVariant,
  ]);

  const pickCustomField = useCallback(
    (name: string) => {
      return customFields.find((o) => o.name === name);
    },
    [customFields]
  );

  return { customFields, pickCustomField };
};

export const useProductField = (product: Product | null, field: string) =>
  useMemo(() => {
    const item = product?.customFields?.find((item) => item.name === field);
    if (!item) return "";
    return item.value.stringValues?.join(",") || item.value.stringValue;
  }, [product, field]);
